import React from 'react'
import propTypes from 'prop-types'
import { Field, ErrorMessage } from 'formik'
import MuiCheckbox from '@material-ui/core/Checkbox'
import MuiSelect from '@material-ui/core/Select'
import MuiNativeSelect from '@material-ui/core/NativeSelect'
import MenuItem from '@material-ui/core/MenuItem'
import DateUtils from '@date-io/date-fns'
import { format, endOfToday } from 'date-fns'
import {
  DatePicker,
  MuiPickersUtilsProvider
} from '@material-ui/pickers'

import './fields.scss'
import { FormControlLabel, Radio, RadioGroup } from '@material-ui/core'

export { SliderFieldWithLabels } from './Slider'
export { NumberField, NumberFieldWithLabel, FastNumberField } from './NumberFields'
export { ToggleFieldWithLabel } from './ToggleFields'

export const InputField = ({ name, value, disabled, className, withoutError }) =>
  <div className={`form-field app-form__field${attachClassName(className)}`}>
    <Field name={name} disabled={disabled} />
    {!withoutError &&
      <ErrorMessage component='span' name={name} />
    }
  </div>

export const InputFieldWithLabel = ({
  label, name, readOnly, value, style,
  withoutError, type, subLabel, as, rows, autoFocus
}) => {
  let fieldProps = { name, as }
  if (readOnly) {
    fieldProps.readOnly = readOnly
    fieldProps.value = readOnly && value
  } else {
    fieldProps.type = type || 'text'
  }
  if (as && as === 'textarea') {
    fieldProps.rows = rows || 2
  }
  if (autoFocus) {
    fieldProps.autoFocus = true
  }

  return (
    <div style={style} className='form-field app-form__field'>
      <label>{label}</label>
      {subLabel &&
        <p className='sublabel'> {subLabel} </p>
      }
      {<Field {...fieldProps} />}
      {!withoutError &&
        <ErrorMessage component='span' name={name} />
      }
    </div>
  )
}

InputFieldWithLabel.propTypes = {
  name: propTypes.string.isRequired,
  label: propTypes.string.isRequired,
  readOnly: propTypes.bool,
  value: propTypes.any,
  type: propTypes.string,
}

export const CheckboxWithLabel = ({ label, name, size, style }) =>
  <div style={style} className={`form-field app-form__check-box ${size ? size : ''}`}>
    <Field name={name}
      render={({ field }) =>
        <MuiCheckbox id={name} {...field} checked={field.value} size={size} />
      }
    />
    <label htmlFor={name}>{label}</label>
    <ErrorMessage className='app-form__field-error' component='span' name={name} />
  </div>

export const Select = ({ name, options, className }) =>
  <div className={`form-field app-form__select${attachClassName(className)}`}>
    <Field name={name}
      render={({ field }) =>
        <MuiSelect className='select' {...field} >
          {options.map((option, idx) => <MenuItem key={idx} value={option.value}>{option.name}</MenuItem>)}
        </MuiSelect>
      }
    />
    <ErrorMessage className='app-form__field-error' component='span' name={name} />
  </div>

export const SelectWithLabel = ({ name, options, label, className, onChange, subLabel, disabled }) =>
  <div className={`form-field app-form__select${attachClassName(className)}`}>
    <label>{label}</label>
    {subLabel &&
      <small className='sublabel'> {subLabel} </small>
    }
    <Field name={name}
      render={({ field }) =>
        <MuiSelect className='select' {...field} onChange={onChange || field.onChange} disabled={disabled} >
          {options.map((option, idx) => <MenuItem key={idx} value={option.value}>{option.name}</MenuItem>)}
        </MuiSelect>
      }
    />
    <ErrorMessage className='app-form__field-error' component='span' name={name} />
  </div>

export const NativeSelectWithLabel = ({ name, options, label, className, onChange, subLabel, disabled }) =>
  <div className={`form-field app-form__select${attachClassName(className)}`}>
    <label>{label}</label>
    {subLabel &&
      <small className='sublabel'> {subLabel} </small>
    }
    <Field name={name}
      render={({ field }) =>
        <MuiNativeSelect className='select' {...field} onChange={onChange || field.onChange} disabled={disabled} >
          {options.map((option, idx) => <option key={idx} value={option.value}>{option.name}</option>)}
        </MuiNativeSelect>
      }
    />
    <ErrorMessage className='app-form__field-error' component='span' name={name} />
  </div>

export const RadioField = ({ name, value, options, className, onChange }) =>
  <div className={`form-field app-form__radio${attachClassName(className)}`}>
    <RadioGroup name={name} value={value} onChange={(_, value) => onChange(value, _)}>
      {options.map(({value, label, ...rest}, index) =>
        <FormControlLabel key={`radio-field-option-${index}`} value={value} control={<Radio />} label={label} {...rest} />
      )}
    </RadioGroup>
  </div>


/*
 * The Formik `ErrorMessage` component was not rendering the errors, so I've
 * rendered it manually here.
 */
export const FileFieldWithLabel = ({
  label, name, values, setValues, errors, touched, setFieldTouched
}) => {
  const updateFormValue = event => {
    setFieldTouched(name, true, true)
    setValues({
      ...values,
      [name]: event.currentTarget.files[0]
    })
  }
  const error = errors[name]
  const isTouched = touched[name]

  return (
    <div className='form-field app-form__field'>
      <label>{label}</label>
      <input name={name} type='file' onChange={updateFormValue} required />
      {isTouched && error && <span>{error}</span>}
    </div>
  )
}

/*
 * The Formik `ErrorMessage` component was not rendering the errors, so I've
 * rendered it manually here.
 *
 * @params:
 * - `dateFormat` now allows us to specify a different date format for the
 *   date picker. The default format is MMMM do (December 1st) but this has
 *   now been overriden to yyyy-MM-dd, so when a dateFormat is not specified
 *   it uses the new default
 *
 * Options:
 * - `successMessage` to be shown after the field has been touched and
 *   there is no validation error on the field.
 */
export const DateFieldWithLabel = ({
  label, name, values, setValues, errors, touched, successMessage, setFieldTouched, dateFormat
}) => {
  const updateFormValue = date => {
    setFieldTouched(name, true, true)
    setValues({
      ...values,
      [name]: format(date, 'yyyy-MM-dd')
    })
  }
  const error = errors[name]
  const isTouched = touched[name]

  return (
    <div className='form-field app-form__field app-form__date-field'>
      <label>{label}</label>
      <MuiPickersUtilsProvider utils={DateUtils}>
        <DatePicker name={name} value={values[name] || endOfToday()} onChange={updateFormValue} format={dateFormat || 'yyyy-MM-dd'} />
      </MuiPickersUtilsProvider>
      {isTouched && error && <span>{error}</span>}
      {successMessage && isTouched && !error && <span className='app-form__field--success-message'>{successMessage}</span>}
    </div>
  )
}

export const FieldError = ({ name }) =>
  <ErrorMessage className='app-form__field-error' component='span' name={name} />

const attachClassName = (className) =>
  className ? ` ${className}` : ''
